import { memo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
// Local files
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useTemplates from 'hooks/useTemplates';
import useSuccess from 'hooks/useSuccess';
import { templatesSelectors } from 'features/templates';
import { checkEmptyString } from 'helpers';

const TemplateDrawer = ({ open, onClose }) => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));

  const { updateTemplate } = useTemplates();
  const { setSuccess } = useSuccess();
  const fetchedTemplate = useCustomSelector(templatesSelectors.getTemplateData);
  const templateIsFetching = useCustomSelector(templatesSelectors.templateIsFetching);
  const [name, setName] = useState('');
  const [fontSize, setFontSize] = useState('');

  const handleEntering = () => { 
    setName(fetchedTemplate?.name || '');
    setFontSize(fetchedTemplate?.font_size || '');
  };
  const handleExited = () => { 
    setName('');
    setFontSize('');
  };
  const needToUpdate = (a,b) => a.name !== b.name || a.font_size !== b.font_size;
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(name)) {
      setName(prev => ({ ...prev, error: `Name can't be empty` }) );
      hasError = true;
    }

    if (hasError) return;

    const template = { id: fetchedTemplate.id, name, font_size: Number(fontSize) || null};

    if (needToUpdate(fetchedTemplate, template)) {
      updateTemplate(template)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Template successfully updated');
          onClose();
        }
      })
    } else {  
      onClose();
    }
  };

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      SlideProps={{ onExited: handleExited, onEntering: handleEntering }}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={onClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
          {!fetchedTemplate ? 'Create' : 'Edit'} Template
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        <TextField
          fullWidth
          margin='normal'
          label='Name'
          disabled={templateIsFetching}
          value={name}
          onChange={({ target: { value } }) => setName(value)}
        />
        <TextField
          fullWidth
          margin='normal'
          type='number'
          label='Font Size'
          disabled={templateIsFetching}
          value={fontSize ?? ''}
          onChange={({ target: { value } }) => {
            const parsed = Number(value);
            setFontSize(value === '' ? null : Math.max(parsed, 1));
          }}
          inputProps={{ min: 1 }}
        />
        <Button
          variant='contained'
          sx={{ display: 'flex', mx: 'auto', px: 5, mt: 3 }}
          disabled={templateIsFetching}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <TemplateDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);